import type { DailyCall } from "@daily-co/daily-js"
import type { CallTransport } from "./callTransport"
import emojiRegex from "emoji-regex"

/**
 * In-call signals that Daily Prebuilt provides out of the box and the custom surface sends
 * over Daily app messages and participant user data instead: reactions, raised hands, and
 * room chat. Everything here is ephemeral (nothing is stored), exactly like Prebuilt.
 */

/** Common reactions shown before opening the full searchable emoji picker. */
export const REACTION_EMOJI = ["👏", "👍", "👎", "👋", "❤️", "😂", "🤯", "🔥", "🎉"] as const
export type ReactionEmoji = (typeof REACTION_EMOJI)[number]

export const CHAT_MESSAGE_TYPE = "wisefriend.call.chat"
export const REACTION_MESSAGE_TYPE = "wisefriend.call.reaction"
export const LOWER_HAND_MESSAGE_TYPE = "wisefriend.call.lower-hand"
/** A stored direct message was just sent; recipients refresh and notify (a second transport beside the panel socket). */
export const DIRECT_MESSAGE_SIGNAL_TYPE = "wisefriend.call.direct-message"

/** Prebuilt's chat limit ("256 char limit"). */
export const CHAT_MESSAGE_MAX_LENGTH = 256
/** How long a floating reaction stays on the stage. */
export const REACTION_DISPLAY_MS = 4000
/** How many reactions can float at once before the oldest is dropped. */
export const MAX_ACTIVE_REACTIONS = 12

export type ChatAppMessage = { type: typeof CHAT_MESSAGE_TYPE; id: string; text: string; sentAt: number }
export type ReactionAppMessage = { type: typeof REACTION_MESSAGE_TYPE; id: string; emoji: string }
export type LowerHandAppMessage = { type: typeof LOWER_HAND_MESSAGE_TYPE }
export type DirectMessageSignal = { type: typeof DIRECT_MESSAGE_SIGNAL_TYPE; threadId: string; senderIdentityKey: string; recipientIdentityKey: string }

/** Raised hands travel in Daily participant user data so every attendee, including late joiners, sees them. */
export type HandUserData = { handRaisedAt?: number | null }

export const readHandRaisedAt = (userData: unknown): number | null => {
  if (!userData || typeof userData !== "object") return null
  const value = (userData as HandUserData).handRaisedAt
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

/** Reactions that take a Fitzpatrick skin-tone modifier (the hand emoji). */
export const SKIN_TONE_REACTIONS: ReadonlySet<string> = new Set(["👏", "👍", "👎", "👋"])
export const SKIN_TONE_MODIFIERS = ["🏻", "🏼", "🏽", "🏾", "🏿"] as const
const SKIN_TONE_PATTERN = /[\u{1F3FB}-\u{1F3FF}]$/u

/** The base quick reaction (skin tone removed), or null for other emoji. */
export const baseReactionEmoji = (value: unknown): ReactionEmoji | null => {
  if (typeof value !== "string") return null
  const base = value.replace(SKIN_TONE_PATTERN, "")
  return (REACTION_EMOJI as readonly string[]).includes(base) ? (base as ReactionEmoji) : null
}

// Match the whole Unicode sequence, including flags, modifiers, and ZWJ families. Removing
// the global flag keeps successive incoming messages independent of RegExp.lastIndex.
const singleEmojiPattern = new RegExp(`^(?:${emojiRegex().source})$`)
export const REACTION_EMOJI_MAX_LENGTH = 64
export const isReactionEmoji = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= REACTION_EMOJI_MAX_LENGTH && singleEmojiPattern.exec(value)?.[0] === value

/** Apply the attendee's default skin tone to reactions that support one. */
export const applyReactionSkinTone = (emoji: ReactionEmoji, tone: string) =>
  (SKIN_TONE_MODIFIERS as readonly string[]).includes(tone) && SKIN_TONE_REACTIONS.has(emoji) ? `${emoji}${tone}` : emoji

export const createSignalId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

/** daily-js keys the local participant as "local", so look remote senders up by session id. */
export const findParticipant = (daily: Pick<DailyCall | CallTransport, "participants"> | null | undefined, sessionId: string | null | undefined) => {
  if (!daily || !sessionId) return null
  try {
    const participants = daily.participants() as Record<string, any>
    return Object.values(participants).find((participant) => participant?.session_id === sessionId) || null
  } catch {
    return null
  }
}

export const participantDisplayName = (daily: Pick<DailyCall | CallTransport, "participants"> | null | undefined, sessionId: string | null | undefined, fallback = "Someone") => {
  const participant = findParticipant(daily, sessionId)
  const name = typeof participant?.user_name === "string" ? participant.user_name.trim() : ""
  return name || fallback
}

export const clampChatText = (text: unknown): string | null => {
  if (typeof text !== "string") return null
  const trimmed = text.trim().slice(0, CHAT_MESSAGE_MAX_LENGTH)
  return trimmed.length > 0 ? trimmed : null
}
